import type { ProblemContext } from '..';
import { lineFont, lineFontSize, READ_ALOUD_HIGHLIGHT_COLOR } from '../state';
import { addBold, withBold, withItalic } from '../utils/fontUtil';
import { isNumeric } from '../utils/isNumeric';
import { parseFont } from '../utils/parseFont';

import { Direction } from './direction';
import { MathObject } from './MathObject';
import type { Multi } from './Multi';
import type { Power } from './Power';

type MiddleHit = { index: number; symbol: string };

// MAX_CHARS_IN_LINE is used for the max number of characters allowed for student input
// Note: this value was previously set at 34 characters due to typing on iPad...
// ...we've semi-arbitrarily increased it to 60 to handle longer labels
// Last note: this value now matches the value used in ProblemQuestionBetter.java
const MAX_CHARS_IN_LINE = 60;

export const MIDDLE_SYMBOLS = ['=', '<', '>', '\u2264', '\u2265', '\u2248'];

export const DEFAULT_SOLO_TEXT_WIDTH = 5;
export const DEFAULT_SPACER_TEXT_WIDTH = 3;

const OPTIONAL_EMPTY_TEXT_FILL = '#e6e8ef';
const EMPTY_TEXT_FILL = '#FF6666';

export interface TextDependencies {
  screenMaxTextW: number;
  ctx: any;
  keyDown: any;
  paintCanvas(): void;
  isReadAloud: any;
  getThemeColor: any;
  isTesting: any;
  changedMaybe: any;
  storeCurrentLineXML: () => void;
  moveCursor: any;
  setFocused: any;
  getFocused: () => Text | undefined;
  getProblemContext: () => ProblemContext;
}

// Currently only a placeholder class to model the BasicObject
// that actually gets built in the builder function later in this file.
export class Text extends MathObject {
  problemJS: TextDependencies;

  text = '';
  fontSize: number;
  multiline: boolean;
  maxW: number;
  minWidth = DEFAULT_SOLO_TEXT_WIDTH;
  font: string;
  lines: Array<[string, number]> = [];
  xtraForLowHang = 5;
  lineSpacer = 4;
  cursorIndentChars = 0;
  firstHighlightedChar?: number;

  charLimit?: [string, number]; // [nameForInvalidMessage, charLimit]

  constructor(
    problemJS: TextDependencies,
    x = 0,
    y = 0,
    text: string,
    fontSize: number,
    multiline = false,
    maxW: number,
    // default to legacy behavior of clamping to screenMaxTextW,
    // but allow caller to disable this (used for help)
    clampToScreenMax = true
  ) {
    super(x, y);
    this.problemJS = problemJS;

    this.multiline = multiline;
    this.maxW = maxW;
    if (clampToScreenMax && maxW > problemJS.screenMaxTextW)
      this.maxW = problemJS.screenMaxTextW;

    this.t = 'text';
    this.gmmName = 'text';
    this.text = text ? text : typeof x === 'string' ? x : '';
    this.fontSize = fontSize;

    // Base font. Bold/italic are applied dynamically via ObjectFormat at paint/measure time.
    this.font = fontSize + 'px ' + lineFont;

    this.viewportFill = this.text.length === 0 ? 'red' : undefined;

    this.sizeMe();
  }

  isEmptyAndUnformatted(): boolean {
    return this.text.trim().length === 0 && !this.getObjectFormat();
  }

  /**
   * Effective font for this Text, honoring ObjectFormat.bold / italic when present.
   * This is used BOTH for paint and for all measureText-based sizing/hit-testing.
   */
  private getEffectiveFont(): string {
    const fmt = this.getObjectFormat?.();
    let f = this.font;

    f = withBold(f, fmt?.bold);
    f = withItalic(f, fmt?.italic);

    return f;
  }

  setCharLimit(nameForInvalidMessage: string, limit: number): void {
    this.charLimit = [nameForInvalidMessage, limit];
  }

  countMiddleSymbols(): number {
    return MIDDLE_SYMBOLS.reduce((count, symbol) => {
      let index = -1;

      while ((index = this.text.indexOf(symbol, index + 1)) > -1) {
        // don't count middle symbols if they are the first or last character
        if (index === 0 || index === this.text.length - 1) continue;
        count++;
      }

      return count;
    }, 0);
  }

  findNthMiddleHit(
    text: string,
    middleSymbols: string[],
    which: number
  ): MiddleHit | null {
    if (which < 1) return null;

    const hits: MiddleHit[] = [];

    for (const symbol of middleSymbols) {
      if (!symbol) continue; // defensive
      let from = 0;

      while (from <= text.length) {
        const idx = text.indexOf(symbol, from);

        if (idx === -1) break;
        hits.push({ index: idx, symbol });
        from = idx + symbol.length;
      }
    }

    hits.sort((a, b) => a.index - b.index);

    return hits[which - 1] ?? null;
  }

  pixelsToMiddleSymbol(whichMiddleSymbol?: number): number {
    if (whichMiddleSymbol != null && whichMiddleSymbol <= 0) {
      throw new Error('whichMiddleSymbol must be >= 1');
    }

    if (whichMiddleSymbol == null) {
      if (this.countMiddleSymbols() !== 1) return 0;
      whichMiddleSymbol = 1;
    }

    const hit = this.findNthMiddleHit(
      this.text,
      MIDDLE_SYMBOLS,
      whichMiddleSymbol
    );

    if (!hit) return 0;

    const ctx = this.problemJS.ctx;

    ctx.save();
    ctx.font = this.getEffectiveFont();

    const pre = this.text.substring(0, hit.index);
    const wPre = ctx.measureText(pre).width;
    const wSymbol = ctx.measureText(hit.symbol).width;

    ctx.restore();

    return wPre + wSymbol / 2;
  }

  setFontSize(fontSize: number): void {
    this.setFont(fontSize + 'px ' + lineFont);
  }

  sizeMe(dontTellParent?: boolean): void {
    this.lines = [];
    const ctx = this.problemJS.ctx;

    ctx.font = this.getEffectiveFont();

    if (this.multiline) {
      this.lines = getMultipleLines(this.text, this.maxW, ctx);
    } else {
      const singW = ctx.measureText(this.text).width;

      if (singW <= this.maxW) {
        this.lines.push([this.text, singW]);
        this.xtraForLowHang = 0;
      } else {
        this.lines = getMultipleLines(this.text, this.maxW, ctx);
      }
    }

    const h =
      this.lines.length * (this.fontSize + this.xtraForLowHang) +
      (this.lines.length - 1) * this.lineSpacer;
    let w = 0;

    for (let i = 0; i < this.lines.length; i++) {
      const lineW = this.lines[i][1];

      if (lineW > w) {
        w = lineW;
      }
    }

    if (w < this.minWidth) {
      w = this.minWidth;
    }

    this.viewportFill = undefined;

    this.viewportW = w;
    this.viewportH = h;
    this.width = w;
    this.height = h;

    if (
      this.parent &&
      this.getValueParent()?.childSizeChanged &&
      !dontTellParent
    ) {
      this.getValueParent()?.childSizeChanged();
    }
  }

  setCursorIndentChars(i: number, right?: boolean): void {
    this.cursorIndentChars = i;

    if (i === 0 && this.parent) {
      const pp = this.parent.parent as Power | undefined;

      if (pp?.t === 'power' && pp.base === this.parent) {
        if (!right && pp.parent && this.text.length > 0) {
          (pp.parent as Multi)!.moveCursor(pp, 'left');
        } else if (right && this.text.length > 0) {
          this.cursorIndentChars = 1;
        }
      }
    }
  }

  getLineX(): number {
    return (
      (this.parent as Multi)!.getLineX() +
      this.viewportX +
      this.getPixelsToCursor()
    );
  }

  getPixelsToCursor(): number {
    const ctx = this.problemJS.ctx;

    ctx.font = this.getEffectiveFont();

    if (this.text.length === 0 || this.cursorIndentChars === 0) {
      return 0;
    } else {
      const t = this.text.substring(0, this.cursorIndentChars);

      return ctx.measureText(t).width;
    }
  }

  paintHighlightForReadAloud(ctx: CanvasRenderingContext2D): void {
    if (!this.problemJS.isReadAloud()) return;

    let firstHighlightedChar = this.firstHighlightedChar;

    if (firstHighlightedChar === undefined) return;

    const lineHeight = this.fontSize + this.xtraForLowHang + this.lineSpacer;

    for (let i = 0; i < this.lines.length; i++) {
      const currentLine = this.lines[i][0];

      if (firstHighlightedChar < currentLine.length) {
        const top = i * lineHeight;
        const startHighlight = ctx.measureText(
          currentLine.substring(0, firstHighlightedChar)
        ).width;

        const remainderOfLine = currentLine.substring(firstHighlightedChar);
        let indexOfEnd = remainderOfLine.indexOf(' ');

        if (indexOfEnd < 0) indexOfEnd = remainderOfLine.length;

        const word = remainderOfLine.substring(0, indexOfEnd);
        const highlightWidth = ctx.measureText(word).width;

        const store = ctx.fillStyle;

        ctx.fillStyle = READ_ALOUD_HIGHLIGHT_COLOR;
        ctx.fillRect(
          startHighlight,
          top,
          highlightWidth,
          this.fontSize + this.xtraForLowHang
        );

        ctx.fillStyle = store;

        break;
      }

      // Logic seems to NOT need this '+1', but reality and testing
      // show that it does. Haven't figured out why.
      firstHighlightedChar -= currentLine.length + 1;
    }
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    let col;

    if (!col && this.text.trim().length === 0) {
      const parentLine = this.parent?.parent as
        | { t?: string; lineNumber?: unknown }
        | undefined;

      if (this.isFocused()) col = this.problemJS.getThemeColor();
      else if (
        !this.parent ||
        parentLine?.t !== 'line' ||
        parentLine.lineNumber !== undefined
      ) {
        if (this.e && this.viewportW > 4) {
          if (
            !(
              !this.problemJS.isTesting() &&
              this.line &&
              this.line.normalGetter &&
              this.line.normalGetter.st === 'c'
            )
          ) {
            if (this.getValueParent()!.isOptional)
              col = OPTIONAL_EMPTY_TEXT_FILL;
            else col = EMPTY_TEXT_FILL;
            // if the student selected a button, we don't want to show the red rectangle
            // in front of the text as it can be confused for an indicator of correctness
            if ((this.line?.locationParent as any)?.args?.selectedButton)
              col = undefined;
          }
        }
      }
    }

    if (col) {
      ctx.fillStyle = col;

      ctx.fillRect(0, 0, this.viewportW, this.viewportH);

      ctx.restore();
      ctx.save();
    }

    ctx.beginPath();
    ctx.textBaseline = 'middle';

    ctx.fillStyle =
      this.fillStyle || (this.line && this.line.fillStyle) || 'black';

    const normalFont = this.getEffectiveFont();

    ctx.font = normalFont;

    this.paintHighlightForReadAloud(ctx);

    for (let i = 0; i < this.lines.length; i++) {
      const h = i * (this.fontSize + this.xtraForLowHang + this.lineSpacer);
      let currentLine = this.lines[i][0];
      // \u00F7 is the division operator (obelus)
      let divI = currentLine.indexOf('\u00F7');

      if (divI < 0) {
        ctx.fillText(currentLine, 0, h + this.fontSize / 2);
      }
      // let's paint the division operator (obelus) darker to help students who are having trouble
      // distinguishing between obelus and plus on grainy Chromebooks
      else {
        // second index: boolean for bold
        const pieces: Array<[string, boolean]> = [];

        while (divI > -1) {
          if (divI == 0) {
            const next: [string, boolean] = ['\u00F7', true];

            pieces.push(next);
          } else {
            const next: [string, boolean] = [
              currentLine.substring(0, divI),
              false,
            ];

            pieces.push(next);
            const nextD: [string, boolean] = ['\u00F7', true];

            pieces.push(nextD);
          }

          currentLine = currentLine.substring(divI + 1);
          divI = currentLine.indexOf('\u00F7');
        }

        if (currentLine.length > 0) {
          const last: [string, boolean] = [currentLine, false];

          pieces.push(last);
        }

        let xPos = 0;
        const restoreFont = ctx.font;

        const forcedBoldFont = addBold(normalFont);

        for (let loopy = 0; loopy < pieces.length; loopy++) {
          const str = pieces[loopy][0];
          const makeBold = pieces[loopy][1];

          ctx.font = makeBold ? forcedBoldFont : normalFont;
          ctx.fillText(str, xPos, h + this.fontSize / 2);
          xPos += ctx.measureText(str).width;
        }

        ctx.font = restoreFont;
      }
    }

    //* *Assumes single line**
    if (this.isFocused()) {
      if (
        !(
          typeof this.cursorIndentChars === 'number' &&
          this.cursorIndentChars >= 0
        )
      ) {
        this.setCursorIndentChars(this.text.length);
      }

      ctx.beginPath();
      ctx.strokeStyle = 'green';
      ctx.lineWidth = 2;
      const i = this.cursorIndentChars;
      let x = ctx.measureText(this.text.substring(0, i)).width;

      x += i === 0 ? 1 : i < this.text.length ? -1 : 1;
      ctx.moveTo(x, -1);
      ctx.lineTo(x, this.fontSize + 1);
      ctx.stroke();
    }

    ctx.restore();
  }

  setHighlightReadAloud(start: any): void {
    this.firstHighlightedChar = start;
  }

  getPaintedCharCount(): number {
    return this.text.length;
  }

  clearHighlightReadAloud(): void {
    this.firstHighlightedChar = undefined;
  }

  setEditable(b: boolean): void {
    this.e = b;
  }

  setText(t: string): void {
    this.text = t;
    this.setCursorIndentChars(t.length);
    this.sizeMe();
  }

  nextLine(): void {
    if (this.line && this.line.normalGetter) {
      this.line.normalGetter.nextLine();
    }
  }

  setFont(font: any): void {
    const { fontSize } = parseFont(font);

    this.fontSize = parseInt(fontSize, 10);
    this.font = font;
    this.sizeMe();
  }

  setToBold(): void {
    // Legacy behavior preserved: this directly modifies the base font string.
    // ObjectFormat.bold (if present) will still be honored via getEffectiveFont().
    this.font = 'bold ' + this.fontSize + 'px ' + lineFont;
    this.sizeMe();
  }

  toXML(): string {
    if (this.text.length === 0) {
      return '';
    } else {
      return '<t>' + this.text + '</t>';
    }
  }

  swapString(old: any, replacer: any): void {
    const i = this.text.indexOf(old);

    if (i > -1) {
      const swap =
        this.text.substring(0, i) +
        replacer +
        this.text.substring(i + old.length);

      this.setText(swap);
    }
  }

  setLine(line: any): void {
    this.line = line;
  }

  grabFocus(direction?: Direction): void {
    this.problemJS.setFocused(this);

    if (direction === 'left') {
      this.setCursorIndentChars(this.text.length);
    } else {
      this.setCursorIndentChars(0, true);
    }
  }

  rebuildFromNativeInput(txt: any): void {
    this.setText('');

    for (let x = 0; x < txt.length; x++) {
      this.problemJS.keyDown(
        { key: txt[x], preventDefault: () => {}, stopPropagation: () => {} },
        true
      );
    }

    this.problemJS.paintCanvas();
  }

  isFocused(): boolean {
    return this === this.problemJS.getFocused();
  }

  getVertToBaseline(): number {
    return this.fontSize / 2;
  }

  mouseDownResponse(x: number, _y: number): boolean {
    const notok =
      !this.e ||
      (this.line &&
        (!this.line.e ||
          (this.line.normalHolder && !this.line.normalHolder.e)));

    if (notok) {
      return false;
    }

    const ctx = this.problemJS.ctx;

    ctx.font = this.getEffectiveFont();
    const allText = this.text;

    if (allText.length === 0) {
      this.setCursorIndentChars(0);
    } else {
      let text = '';
      let index = 0;
      let done = false;

      while (!done) {
        const letter = allText.substring(index, index + 1);

        text += letter;
        const tw = ctx.measureText(text).width;

        if (x <= tw) {
          const lw = ctx.measureText(letter).width;
          const middle = tw - lw / 2;

          if (x > middle) {
            index++;
          }

          this.setCursorIndentChars(index);
          done = true;
        } else {
          index++;

          // maybe we just did last letter
          if (index >= allText.length) {
            this.setCursorIndentChars(index);
            done = true;
          }
        }
      }
    }

    this.problemJS.setFocused(this);
    this.problemJS.paintCanvas();

    // ie, consume event, no mouse reaction by parents!
    return true;
  }

  checkForMergeWithPowerToRight(): void {
    if (!this.isBase()) {
      if (this.getValueParent()!.t === 'm') {
        const r = (this.parent as Multi)!.getElementToRight(this);

        if (r?.t === 'power') {
          const power = r as Power;

          if (!power.hasDigitBase()) return;
          let chop = '';
          let chopI = -1;
          let foundDot = false;

          for (let ii = this.text.length - 1; ii > -1; ii--) {
            const snip = this.text.substring(ii, ii + 1);

            if (isNumeric(snip) || (!foundDot && snip == '.') || snip == ',') {
              chop = snip + chop;
              chopI = ii;
              if (snip == '.') foundDot = true;
            } else {
              break;
            }
          }

          if (chop.length > 0) {
            const ot = power.base.children[0] as Text;

            this.text = chopI < 1 ? '' : this.text.substring(0, chopI);
            ot.text = chop + ot.text;
            ot.sizeMe();
            ot.grabFocus();
            ot.setCursorIndentChars(chop.length);
          }
        }
      }
    }
  }

  insertString(string: string, skipRepaint: boolean): void {
    if ((string + this.text).length > MAX_CHARS_IN_LINE) {
      return;
    }

    // limit the number of Text characters in the exponent of a Power (excluding spaces)
    // Note: For now, only limits Texts that are direct children of the exponent Multi.
    if (
      this.charLimit !== undefined &&
      (string + this.text).replace(/ /g, '').length > this.charLimit[1]
    ) {
      this.problemJS.getProblemContext().addStackableDialog?.({
        msg:
          "GMM won't allow more than " +
          this.charLimit[1] +
          ' characters in the ' +
          this.charLimit[0] +
          '.',
        top: 'Reached Limit',
      });

      return; // don't add 'string' to 'this.text'
    }

    if (string === '-') {
      string = '\u2212';
    }

    if (
      !(
        typeof this.cursorIndentChars === 'number' &&
        this.cursorIndentChars >= 0
      )
    ) {
      this.setCursorIndentChars(this.text.length);
    }

    const pre = this.text.substring(0, this.cursorIndentChars);
    const post = this.text.substring(this.cursorIndentChars);

    if (string === ' ') {
      if (pre.length > 0 && pre.substring(pre.length - 1) === ' ') return;
      if (post.length > 0 && post.substring(0, 1) === ' ') return;
    }

    if (
      (this.parent!.parent as MathObject)!.t === 'subscript' ||
      (this.parent!.parent as MathObject)!.t === 'function'
    ) {
      // Check for equals and push out to the right
      if (!isAlphaNumeric(string)) {
        (this.parent!.parent!.parent as Multi).pushTextOutToRight(
          this.parent!.parent as MathObject,
          string
        );
      } else if (this.cursorIndentChars == 0 && this.text.length > 0) {
        (this.parent!.parent!.parent as Multi).pushTextOutOfBaseToLeft(
          this.parent!.parent as MathObject,
          string
        );
      } else {
        this.text = pre + string + post;
        this.setCursorIndentChars(this.cursorIndentChars + 1);
      }

      this.sizeMe();
      this.problemJS.paintCanvas();

      return;
    }

    this.text = pre + string + post;
    this.checkForMergeWithPowerToRight();

    // normal situation: advance cursor to immediately following insert
    this.setCursorIndentChars(this.cursorIndentChars + (string + '').length);

    this.sizeMe();

    this.problemJS.changedMaybe();

    if (!skipRepaint) this.problemJS.paintCanvas();
  }

  deleteString(start: any, len: any): void {
    this.text =
      this.text.substring(0, start) + this.text.substring(start + len);

    this.checkForMergeWithPowerToRight();

    this.problemJS.changedMaybe();
  }

  deletePressed(): void {
    if (
      !(
        typeof this.cursorIndentChars === 'number' &&
        this.cursorIndentChars >= 0
      )
    ) {
      this.setCursorIndentChars(this.text.length);
    }

    if (this.cursorIndentChars == this.text.length) {
      if (this.parent) {
        (this.parent as Multi).deleteRight(this);
      }
    } else {
      this.deleteString(this.cursorIndentChars, 1);
      this.sizeMe();
    }

    this.problemJS.storeCurrentLineXML();

    this.problemJS.changedMaybe();
  }

  backspace() {
    if (
      !(
        typeof this.cursorIndentChars === 'number' &&
        this.cursorIndentChars >= 0
      )
    ) {
      this.setCursorIndentChars(this.text.length);
    }

    if (this.cursorIndentChars === 0) {
      if (this.isBase()) {
        this.problemJS.moveCursor('left');
        this.problemJS.getFocused()?.backspace();
        this.problemJS.moveCursor('right');
      } else if (this.parent) {
        (this.parent as Multi).deleteLeft(this);
      }
    } else {
      this.deleteString(this.cursorIndentChars - 1, 1);
      this.setCursorIndentChars(this.cursorIndentChars - 1);
      this.sizeMe();
    }

    this.problemJS.storeCurrentLineXML();
    this.problemJS.changedMaybe();
  }

  clearMyLine(): void {
    if (!this.line) return;
    this.line.clear();
    this.problemJS.changedMaybe();
  }

  slideCursor(dx: number): boolean {
    if (
      !(
        typeof this.cursorIndentChars === 'number' &&
        this.cursorIndentChars >= 0
      )
    ) {
      this.setCursorIndentChars(0);
    }

    if (this.cursorIndentChars === 0 && dx < 0) {
      return (this.parent as Multi).moveCursor(this, 'left');
    }

    if (this.cursorIndentChars >= this.text.length && dx > 0) {
      return (this.parent as Multi).moveCursor(this, 'right');
    }

    this.setCursorIndentChars(this.cursorIndentChars + dx);

    return true;
  }

  isBase(): boolean {
    return !!(this.parent && this.parent.gmmName === 'base multi');
  }
}

const isAlphaNumeric = function (str: string): boolean {
  let code, i, len;

  for (i = 0, len = str.length; i < len; i++) {
    code = str.charCodeAt(i);

    if (
      !(code > 47 && code < 58) && // numeric (0-9)
      !(code > 64 && code < 91) && // upper alpha (A-Z)
      !(code > 96 && code < 123) && // lower alpha (a-z)
      !(code == 960) // Pi
    ) {
      return false;
    }
  }

  return true;
};

const getMultipleLines = function (
  string: string,
  maxW: number,
  ctx: CanvasRenderingContext2D
) {
  const ret: Array<[string, number]> = [];

  const words: string[] = [];

  while (string.length > 0) {
    let x = 0;
    let found = false;
    let end = false;

    while (!found && !end) {
      const s = string.substring(x, x + 1);

      if (s === ' ') {
        found = true;
      }

      x++;

      if (x === string.length) {
        end = true;
      }
    }

    let word = '';

    if (!end) {
      word = string.substring(0, x - 1);
      string = string.substring(x);
    } else {
      word = string;
      string = '';
    }

    words.push(word);
  }

  let line = words[0];
  let lastW = ctx.measureText(line).width;

  for (let x = 1; x < words.length; x++) {
    const maybe = line + ' ' + words[x];
    const metrics = ctx.measureText(maybe);

    if (metrics.width > maxW) {
      ret.push([line, lastW]);
      line = words[x];
      lastW = 0;
    } else {
      line = maybe;
      lastW = metrics.width;
    }
  }

  ret.push([line, lastW]);

  return ret;
};

export function buildText(
  problemJS: TextDependencies,
  x = 0,
  y = 0,
  t = '',
  fontSize = lineFontSize,
  multiline = false,
  maxW = problemJS.screenMaxTextW,
  clampToScreenMax = true
): Text {
  return new Text(
    problemJS,
    x,
    y,
    t,
    fontSize,
    multiline,
    maxW,
    clampToScreenMax
  );
}
